import { GetPersonUser, PersonBenefits } from '@esportal/portal-service';
import React from 'react';
import { PortalContext } from '../../../../../context/portal-context';
import { discountHelper } from '../../../../../helpers/discountHelper';
import { getEmail, getSurname1, getSurname2, getUserName } from '../../../../../helpers/nameHelper';
import { getHuchaTierFromDiscounts } from '../../hucha-venta/helpers/get-renovador-tier';
import { hasDGTLabelInfo } from '../../hucha-venta/helpers/has-dgt-label-info';
import { HuchaPosventaValues, HuchaType, HuchaValues, HuchaVentaValues } from '../models/hucha-values';

export const HuchaDiscountNameByType: Record<HuchaType, string> = {
	venta: 'moneybox',
	posventa: 'bonoTaller50',
};

export const hasHucha = (personBenefits: PersonBenefits): boolean => {
	const discounts = personBenefits.benefits.discounts ?? [];

	return discounts.some(
		({ name }) => name === HuchaDiscountNameByType.venta || name === HuchaDiscountNameByType.posventa,
	);
};

export const useHuchaValues = (
	userData: GetPersonUser | null,
	personBenefits: PersonBenefits,
	huchaType: HuchaType,
): HuchaValues | null => {
	const { formatNumberWithThousands } = discountHelper();
	const discounts = personBenefits.benefits.discounts ?? [];

	return React.useMemo(() => {
		const getDiscountByName = (name: string) => discounts.find((discount) => discount.name === name);

		// datos base
		const baseData = {
			idUser: userData?.portalUser?.data?.clubVW?.idMember || '',
			name: userData ? getUserName(userData) : '',
			apellido1: userData ? getSurname1(userData) : '',
			apellido2: userData ? getSurname2(userData) : '',
			email: userData ? getEmail(userData) : '',
			fechaDescarga: new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'numeric', year: 'numeric' }),
		};

		const huchaDiscount = getDiscountByName(HuchaDiscountNameByType[huchaType]);

		if (!huchaDiscount) {
			return null;
		}

		const rawHuchaValue = huchaDiscount?.value.toString() || '0';
		const dineroHucha = formatNumberWithThousands(rawHuchaValue);

		// rama posventa
		if (huchaType === 'posventa') {
			return {
				...baseData,
				huchaType,
				dineroHucha,
				status: huchaDiscount.voucher?.status,
				expirationDate: huchaDiscount.voucher?.vgedExpirationDate,
			} as HuchaPosventaValues;
		}

		// rama venta
		const huchaTier = getHuchaTierFromDiscounts(discounts);

		// hucha venta estándar
		if (huchaTier === 'standard') {
			return {
				...baseData,
				huchaType,
				huchaTier,
				dineroHucha,
			} as HuchaVentaValues;
		}

		// hucha venta renovadores
		const renovadoresDiscount = getDiscountByName(huchaTier);
		const renovadoresRaw = renovadoresDiscount?.value.toString() || '0';
		const dineroRenovadores = formatNumberWithThousands(renovadoresRaw);

		const huchaNum = parseInt(rawHuchaValue.replace(/\./g, '')) || 0;
		const renoNum = parseFloat(renovadoresRaw) || 0;
		const descuentoTotal = formatNumberWithThousands((huchaNum + renoNum).toString());

		// etiqueta DGT para renovadorPlus10
		const { portalService } = React.useContext(PortalContext);
		let DGTLabelInfo = false;

		if (huchaTier === 'renovadorPlus10') {
			const vgedVin = renovadoresDiscount?.voucher?.vgedVin;

			if (vgedVin) {
				const vehicle = portalService.getVehicleOrOrderByVin(vgedVin).getValue();
				DGTLabelInfo = hasDGTLabelInfo(vehicle);
			}
		}

		return {
			...baseData,
			huchaType,
			huchaTier,
			DGTLabelInfo,
			dineroHucha,
			dineroRenovadores,
			descuentoTotal,
		} as HuchaVentaValues;
	}, [userData, discounts, huchaType, formatNumberWithThousands]);
};
